/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Map as ImmutableMap, List } from 'immutable';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIToggle from 'UIComponents/input/UIToggle';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UITable from 'UIComponents/table/UITable';
import UIStatusTag from 'UIComponents/tag/UIStatusTag';
import AdAccountCheckbox from './AdAccountCheckbox';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import { ConnectedAccountStatuses } from 'ads-account-connection/lib/AvailableAdAccountRecord';
import KnowledgeBaseLink from 'ads-account-connection/components/KnowledgeBaseLink';
import { KBArticles } from 'ads-account-connection/lib/Constants';
const AD_ACCOUNTS_PER_PAGE = 5;
const CONNECTED_ACCOUNT_STATUSES_TO_USE = {
  [ConnectedAccountStatuses.ERROR]: 'danger',
  [ConnectedAccountStatuses.INACTIVE]: 'danger',
  [ConnectedAccountStatuses.CONNECTED]: 'success'
};

const AdAccountsModalTableRow = ({
  adAccount,
  maxSelectableAccounts,
  selectedAdAccounts,
  selectedNewAccounts,
  allowDeselect,
  setSelectedAdAccounts
}) => {
  const handleAdAccountChecked = useMemo(() => {
    return ({
      target: {
        checked
      }
    }) => {
      if (checked && !selectedAdAccounts.has(adAccount.adAccountId)) {
        setSelectedAdAccounts(selectedAdAccounts.set(adAccount.adAccountId, adAccount.connected ? adAccount.trackingEnabled : true));
      } else if (!checked && selectedAdAccounts.has(adAccount.adAccountId)) {
        setSelectedAdAccounts(selectedAdAccounts.delete(adAccount.adAccountId));
      }
    };
  }, [adAccount, selectedAdAccounts, setSelectedAdAccounts]);
  const handleAdAccountTrackingToggled = useCallback(() => {
    setSelectedAdAccounts(selectedAdAccounts.set(adAccount.adAccountId, !selectedAdAccounts.get(adAccount.adAccountId)));
  }, [adAccount, selectedAdAccounts, setSelectedAdAccounts]);
  return /*#__PURE__*/_jsxs("tr", {
    "data-test-id": adAccount.adAccountId,
    children: [/*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(AdAccountCheckbox, {
        onChange: handleAdAccountChecked,
        accountDisabled: !adAccount.enabled,
        currentUserHasConnected: adAccount.currentUserHasConnected,
        alreadyConnected: adAccount.connected,
        accountLimitMet: selectedNewAccounts.size >= maxSelectableAccounts,
        checked: selectedAdAccounts.has(adAccount.adAccountId),
        maxSelectableAccounts: maxSelectableAccounts,
        children: adAccount.connected && !allowDeselect ? /*#__PURE__*/_jsxs("span", {
          children: [adAccount.name || adAccount.getFormattedId(), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(UIStatusTag, {
            use: CONNECTED_ACCOUNT_STATUSES_TO_USE[adAccount.connectedAccountStatus],
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ADS_ACCOUNT_CONNECTION.adAccountConnectModal.statuses.${adAccount.connectedAccountStatus || ''}`
            })
          })]
        }) : adAccount.name ? adAccount.name : adAccount.getFormattedId()
      })
    }), /*#__PURE__*/_jsx("td", {
      children: adAccount.getFormattedId()
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(UIToggle, {
        size: "extra-small",
        disabled: !selectedAdAccounts.has(adAccount.adAccountId) || !allowDeselect && adAccount.connected,
        checked: selectedAdAccounts.get(adAccount.adAccountId, false) || adAccount.trackingEnabled,
        onChange: handleAdAccountTrackingToggled
      })
    })]
  });
};

const AdAccountsModalTable = ({
  availableAccounts,
  maxSelectableAccounts,
  allowDeselect,
  selectedAdAccounts,
  selectedNewAccounts,
  setSelectedAdAccounts,
  trackEvent
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const maxPages = Math.ceil(availableAccounts.size / AD_ACCOUNTS_PER_PAGE);
  const onPageChange = useCallback(e => setCurrentPage(e.target.value - 1), []);
  const relevantAvailableAccounts = useMemo(() => availableAccounts.sortBy(adAccount => adAccount.name || adAccount.getFormattedId()).slice(currentPage * AD_ACCOUNTS_PER_PAGE, (currentPage + 1) * AD_ACCOUNTS_PER_PAGE), [availableAccounts, currentPage]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UITable, {
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs("tr", {
          children: [/*#__PURE__*/_jsx("th", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.table.headers.adAccount"
            })
          }), /*#__PURE__*/_jsx("th", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.table.headers.accountId"
            })
          }), /*#__PURE__*/_jsx("th", {
            children: /*#__PURE__*/_jsx(UIHelpIcon, {
              title: /*#__PURE__*/_jsxs("p", {
                children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
                  message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.table.headers.autoTrackingToolTip"
                }), ' ', /*#__PURE__*/_jsx(KnowledgeBaseLink, {
                  kbArticle: KBArticles.AD_TRACKING,
                  context: "ad-account-connection-modal",
                  trackEvent: trackEvent
                })]
              }),
              tooltipProps: {
                use: 'longform'
              },
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.table.headers.autoTracking"
              })
            })
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: relevantAvailableAccounts.map(adAccount => /*#__PURE__*/_jsx(AdAccountsModalTableRow, {
          adAccount: adAccount,
          maxSelectableAccounts: maxSelectableAccounts,
          selectedAdAccounts: selectedAdAccounts,
          selectedNewAccounts: selectedNewAccounts,
          setSelectedAdAccounts: setSelectedAdAccounts,
          allowDeselect: allowDeselect
        }, adAccount.accountId))
      })]
    }), maxPages > 1 && /*#__PURE__*/_jsx(UIPaginator, {
      onPageChange: onPageChange,
      page: currentPage + 1,
      pageCount: maxPages
    })]
  });
};

AdAccountsModalTable.propTypes = {
  availableAccounts: PropTypes.instanceOf(List).isRequired,
  maxSelectableAccounts: PropTypes.number.isRequired,
  selectedAdAccounts: PropTypes.instanceOf(ImmutableMap).isRequired,
  setSelectedAdAccounts: PropTypes.func.isRequired,
  trackEvent: PropTypes.func
};
export default AdAccountsModalTable;